import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import router from '@/router';
import VueCookie from 'vue-cookie';
import {
    isSharedDomain,
    readCookieSession,
    writeSession,
    clearSharedSession,
    clearSsoHandoffCookie,
} from '@/utils/sharedSession';

const $cookie = VueCookie;
const state = {
    user: null,
    sidebar: false,
    uuidv44: null,
    vendorDel: {},
    // True once a shared cross-product cookie has actually been observed in this
    // browser session. Guards the single-logout branch so we never force a logout
    // when the shared cookie simply never persisted (e.g. Secure cookie on a
    // non-HTTPS *.mintmesh.ai dev host).
    sharedSeen: false,
}

const mutations = {
    SET_USER_DATA(state, userData) {
        state.user = userData
        const key = 'mm_auth_key';
        $cookie.set(key, userData.jwt);
        axios.defaults.headers.common['Authorization'] = `Bearer ${$cookie.get(key)}`
        axios.defaults.headers.common['x-request-id'] = uuidv4()
        axios.defaults.headers.common['x-b3-traceid'] = uuidv4()

        const modifiedObject = Object.assign({}, userData);
        delete modifiedObject['jwt'];
        localStorage.setItem('user', JSON.stringify(modifiedObject))

        // Publish to the shared cross-product cookie so Supplier Portal can adopt
        // this session. The cookie carries ONLY the flat inner user object (the
        // shared RBAC identity), NOT this app's {status, message, user} envelope.
        // No-op off a shared domain (localhost/dev unaffected).
        writeSession(userData.jwt, userData.user || null)
        // Only mark "seen" once the cookie has genuinely persisted and is readable.
        if (isSharedDomain() && readCookieSession()) {
            state.sharedSeen = true
        }
    },
    MARK_SHARED_SEEN(state) {
        state.sharedSeen = true
    },
    CLEAR_USER_DATA(state) {
        // Reset Vuex state + drop the auth header explicitly (don't rely on reload).
        state.user = null
        delete axios.defaults.headers.common['Authorization']
        // Clear user + JWT from local storage.
        localStorage.removeItem("user")
        localStorage.clear()
        // Clear the JWT cookie (this app) and the shared token+user cookie (cross
        // product) BEFORE reload so single logout propagates to the other product.
        // No-op off a shared domain.
        $cookie.delete('mm_auth_key');
        clearSharedSession()
        // Also drop the spent SSO hand-off token: the reload below lands on the
        // login page, which would otherwise replay the same failed SSO exchange.
        clearSsoHandoffCookie()
        location.reload()
    },
    // Reload-free session clear, safe to call from inside the router guard.
    // The guard re-reads localStorage['user'] right after and redirects to login.
    SOFT_CLEAR_USER_DATA(state) {
        state.user = null
        localStorage.removeItem("user")
        localStorage.clear()
        clearSharedSession()
        clearSsoHandoffCookie()
        $cookie.delete('mm_auth_key');
        delete axios.defaults.headers.common['Authorization']
    },
    SIDE_BAR(state, value) {
        state.sidebar = value
    },
}

const actions = {
    register({ commit }, credentials) {
        return axios
            .post(`${process.env.VUE_APP_API}/password/set?u=${localStorage.getItem('userID')}&t=${localStorage.getItem('userToken')}`, credentials)
            .then(({ data }) => {
                // console.log('user data is', data)
                commit('SET_USER_DATA', data)
            })
    },
    login({ commit }, credentials) {
        console.log('cred check');
        return axios
            .post(`${process.env.VUE_APP_API}/login`, credentials)
            .then(({ data }) => {
                // console.log(data)
                commit('SET_USER_DATA', data)
                // const notification = {
                //     type: 'success',
                //     message: 'logged In!'
                // }
                // dispatch('notification/add', notification, { root: true })
            })
            .catch(error => {
                // const notification = {
                //   type: 'error',
                //   message: 'There was a problem creating your event: ' + error.message
                // }
                // dispatch('notification/add', notification, { root: true })
                throw error
            })
    },
    forgotpassword({ commit }, credentials) {
        return axios
            .post('/auth/forgot-password', credentials)
            .then(({ data }) => {
                //commit('SET_USER_DATA', data)
                //   dispatch('notification/add', notification, { root: true })
                //   })
                //   .catch(error => {
                //     const notification = {
                //     type: 'error',
                //     message: 'There was a problem: ' + error.message
                //   }
                //   dispatch('notification/add', notification, { root: true })
                //   throw error
            })
    },
    logout({ commit }) {
        return axios
            .post(`${process.env.VUE_APP_API}/logout`)
            .then((response) => {
                if (response.data.status) {
                    commit('CLEAR_USER_DATA')
                    router.replace({ path: '/login' })
                }
                if (response.data.status === 401) {
                    commit('CLEAR_USER_DATA')
                    router.replace({ path: '/login' })
                }
                else {
                    commit('CLEAR_USER_DATA')
                    router.replace({ path: '/login' })
                }
            })
            .catch(err => {
                commit('CLEAR_USER_DATA')
                router.replace({ path: '/login' })
            })
    },
    // Adopt a shared cross-product session into this app's state/storage. Rebuilds
    // this app's existing localStorage envelope ({status, message, user}) around the
    // flat inner user object carried in the cookie, so localStorage['user'] keeps the
    // exact same structure as a normal login. Single source of truth for both adopt
    // paths (boot + reconcile) so they can't drift.
    adoptSharedSession({ commit }, shared) {
        if (!shared || !shared.token) return
        commit('SET_USER_DATA', {
            status: 200,
            message: 'Successfully login',
            user: shared.user || null,
            jwt: shared.token,
        })
    },
    // Live cross-product sync. Called from the router guard (before auth checks,
    // with { navigate: false }) and on window focus/visibilitychange. No-op off a
    // shared domain so localhost/dev is never force-logged-out by a legitimately
    // absent cookie. NEVER reloads or navigates from within the guard — the guard
    // re-reads localStorage right after and redirects itself. Returns true when it
    // cleared the local session (single logout).
    reconcileFromShared({ commit, state, dispatch }, payload) {
        const navigate = !payload || payload.navigate !== false
        if (!isSharedDomain()) return false

        const shared = readCookieSession()
        const localToken = $cookie.get('mm_auth_key') || (state.user && state.user.jwt) || null

        if (shared && shared.token) {
            commit('MARK_SHARED_SEEN')
            // Cookie present and token differs from ours -> adopt it (no navigation;
            // SET_USER_DATA mirrors into localStorage so the guard sees us as logged in).
            if (shared.token !== localToken) {
                dispatch('adoptSharedSession', shared)
            }
            return false
        }

        // Cookie absent. Only a genuine cross-product logout if a shared cookie was
        // actually observed earlier this session -- otherwise the cookie simply never
        // persisted (e.g. non-HTTPS shared host) and we must NOT force a logout.
        const haveSession = !!state.user || !!localStorage.getItem('user')
        if (state.sharedSeen && haveSession) {
            commit('SOFT_CLEAR_USER_DATA')
            if (navigate && router.currentRoute && router.currentRoute.path !== '/login') {
                router.replace({ path: '/login' }).catch(() => {})
            }
            return true
        }
        return false
    },
    rolePrediction({ commit }, credentials) {
        return axios
            .post('/inference/rolePrediction?industry_id=5ceb8d8f005fb9001c582f95', credentials)
            .then((response) => {
                // console.log(credentials)
                localStorage.setItem("role_prediction_id", response.data.role_prediction_id);
                // console.log(response.data.role_prediction_id)
            });
        // .then(({ res }) => {
        //   localStorage.setItem('role_prediction_id', response.role_prediction_id)
        //   console.log('user data is', data)
        //   // commit('SET_USER_DATA', data)
        // })
    },
    getitemprogress({ commit, dispatch }, item) {
        console.log('item-----fromstore', item)
        let progressMR = item.item
        return axios
            .get(`${process.env.VUE_APP_API}/mr/progress?mr_id=${progressMR._id}`)
            .then(({ data }) => {
                console.log(data)
                if (data.extraction_status == 'Failed') {
                    const notification = {
                        type: 'error',
                        message: 'Sorry! Extraction for ' + progressMR.name + ' has been failed.'
                    }
                    dispatch('notification/add', notification, { root: true })
                }
                if (data.extraction_status == 'Completed') {
                    console.log("print Success");
                    const notification = {
                        type: 'success',
                        message: 'Requisition has been completed for  ' + progressMR.name + '.'
                    }
                    dispatch('notification/add', notification, { root: true })
                }
                // if(data.extraction_status == 'In-progress'){
                //   const notification = {
                //     type: 'warning',
                //     message: 'Requisition for  ' + progressMR.name + ' is in-progress.'
                //   }
                //   dispatch('notification/add', notification, { root: true })
                // }
            })
            .catch(error => {
                const notification = {
                    type: 'error',
                    message: 'Error: ' + error.message
                }
                dispatch('notification/add', notification, { root: true })
                throw error
            })
    },
}

const getters = {
    loggedIn(state) {
        return !!state.user
    },
    user(state) {
        return state.user
    },
}
export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}